import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useGoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff } from 'lucide-react';
import { authService } from '../../../lib/api/apiService';
import { useTranslation } from 'react-i18next';
import { getRecaptchaToken, preloadRecaptcha } from '../../../utils/recaptcha';
import RecaptchaLegalNotice from '../../../components/RecaptchaLegalNotice';

const createVendorLoginSchema = (t) =>
    z.object({
        email: z
            .string()
            .trim()
            .min(1, t('vendor_auth.errors.email_required'))
            .email(t('vendor_auth.errors.email_invalid')),
        password: z
            .string()
            .min(1, t('vendor_auth.errors.password_required')),
    });

const Login = () => {
    const { t, i18n } = useTranslation();
    const schema = useMemo(
        () => createVendorLoginSchema(t),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, i18n.language]
    );

    const [showPassword, setShowPassword] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [error, setError] = useState('');

    const [currentSlide, setCurrentSlide] = useState(0);

    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const slides = [
        {
            title: t('vendor_auth.slides.slide1.title'),
            description: t('vendor_auth.slides.slide1.description'),
        },
        {
            title: t('vendor_auth.slides.slide2.title'),
            description: t('vendor_auth.slides.slide2.description'),
        },
        {
            title: t('vendor_auth.slides.slide3.title'),
            description: t('vendor_auth.slides.slide3.description'),
        },
        {
            title: t('vendor_auth.slides.slide4.title'),
            description: t('vendor_auth.slides.slide4.description'),
        },
    ];

    useEffect(() => {
        preloadRecaptcha();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [slides.length]);

    const persistSessionAndRedirect = useCallback((accessToken, user) => {
        if (!accessToken || !user) {
            setError(t('auth.errors.missing_info'));
            return;
        }
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('user', JSON.stringify(user));

        if (user.role === 'SELLER') {
            navigate('/dashboard');
        } else if (user.role === 'ADMIN') {
            navigate('/admin-dashboard');
        } else if (user.role === 'CLIENT') {
            navigate('/');
        }
    }, [navigate, t]);

    const fieldClass = (hasError) =>
        `text-sm w-full px-4 py-3 border rounded-full focus:outline-none focus:ring-2 focus:ring-orange-light focus:border-transparent transition-all duration-200 ${
            hasError ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
        }`;

    const onSubmit = handleSubmit(async (formData) => {
        setError('');

        try {
            const recaptchaToken = await getRecaptchaToken('login');
            const result = await authService.login(
                formData.email.trim().toLowerCase(),
                formData.password,
                recaptchaToken
            );

            if (!result) {
                setError(t('vendor_auth.errors.login_error'));
                return;
            }

            if (result.success === false) {
                setError(result.error || t('vendor_auth.errors.login_error'));
                return;
            }

            const statusCode = result.statusCode ?? result.status ?? result.code;
            const payload = result.data ?? result.payload ?? {};
            const accessToken =
                payload.access_token ?? payload.accessToken ?? result.access_token;
            const user = payload.user ?? result.user;

            if (
                (statusCode && String(statusCode) === '200') ||
                result.success === true ||
                (accessToken && user)
            ) {
                persistSessionAndRedirect(accessToken, user);
            } else {
                setError(result.message || t('vendor_auth.errors.login_error'));
            }
        } catch (err) {
            console.error('Erreur lors de la connexion:', err);
            setError(
                err?.message?.includes('reCAPTCHA') || err?.message?.includes('Token')
                    ? err.message
                    : t('vendor_auth.errors.unexpected_error')
            );
        }
    });

    const handleGoogleLoginSuccess = async (response) => {
        setGoogleLoading(true);
        setError('');

        try {
            const result = await authService.loginWithGoogle(response.access_token);

            if (!result) {
                setError(t('auth.errors.invalid_response'));
                return;
            }

            if (result.success === false) {
                setError(result.error || t('auth.errors.google_error'));
                return;
            }

            const statusCode = result.statusCode ?? result.status ?? result.code;
            const payload = result.data ?? result.payload ?? {};
            const accessToken =
                payload.access_token ?? payload.accessToken ?? result.access_token;
            const user = payload.user ?? result.user;

            if (
                (statusCode && String(statusCode) === '200') ||
                result.success === true ||
                (accessToken && user)
            ) {
                persistSessionAndRedirect(accessToken, user);
            } else {
                setError(result.message || t('auth.errors.google_error'));
            }
        } catch (err) {
            setError(t('auth.errors.google_error'));
            console.error(err);
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleLoginError = () => {
        setError(t('auth.errors.google_auth_error'));
        setGoogleLoading(false);
    };

    const googleLogin = useGoogleLogin({
        onSuccess: handleGoogleLoginSuccess,
        onError: handleGoogleLoginError,
    });

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: 'url("/images/auth/background-image.png")',
                }}
            />

            <div className="relative z-10 flex w-full max-w-6xl justify-between">
                <div className="w-full lg:w-1/2 flex items-center justify-center">
                    <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md">
                        <div className="flex justify-center">
                            <img
                                onClick={() => navigate('/')}
                                src="/images/logo.png"
                                alt="logo"
                                className="w-auto h-14 hover:cursor-pointer"
                            />
                        </div>

                        <div className="text-center mb-8 mt-4">
                            <h2 className="text-xl font-bold text-gray-800 mb-2">
                                {t('vendor_auth.welcome_title')}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {t('vendor_auth.welcome_description')}
                            </p>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={onSubmit} className="space-y-4" noValidate>
                            <div>
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t('vendor_auth.email_label')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    autoComplete="email"
                                    {...register('email')}
                                    placeholder={t('vendor_auth.email_placeholder')}
                                    className={fieldClass(!!errors.email)}
                                />
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.email.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t('vendor_auth.password_label')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        autoComplete="current-password"
                                        {...register('password')}
                                        placeholder={t('vendor_auth.password_placeholder')}
                                        className={`${fieldClass(!!errors.password)} pr-12`}
                                    />
                                    <button
                                        type="button"
                                        tabIndex={-1}
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0"
                                        aria-label={
                                            showPassword
                                                ? t('auth.hide_password')
                                                : t('auth.show_password')
                                        }
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.password.message}
                                    </p>
                                )}
                            </div>

                            <div className="text-right">
                                <Link
                                    to="/forgot-password"
                                    className="text-sm text-orange-light hover:text-orange-hover font-medium transition-colors"
                                >
                                    {t('vendor_auth.forgot_password')}
                                </Link>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting || googleLoading}
                                className={`w-full text-white text-base py-2 px-6 rounded-full transform transition-all duration-200 shadow-lg ${
                                    isSubmitting || googleLoading
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-orange-hover hover:bg-orange-600 hover:scale-105'
                                }`}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center justify-center">
                                        <svg
                                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        {t('vendor_auth.login_loading')}
                                    </div>
                                ) : (
                                    t('vendor_auth.login_button')
                                )}
                            </button>

                            <RecaptchaLegalNotice className="text-center" />

                            <button
                                type="button"
                                onClick={() => googleLogin()}
                                disabled={googleLoading || isSubmitting}
                                className="w-full text-gray-700 text-base py-2 px-6 rounded-full border border-gray-300 flex items-center justify-center gap-2 transform transition-all duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {googleLoading ? (
                                    t('vendor_auth.login_loading')
                                ) : (
                                    <>
                                        <img
                                            src="/images/google-icon.png"
                                            alt="Google"
                                            className="w-5 h-5"
                                        />
                                        {t('auth.login_with_google')}
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="hidden lg:flex lg:w-1/3 items-end justify-end">
                    <div className="text-white max-w-md text-center">
                        <h2 className="text-xl font-medium text-right mb-4">
                            {slides[currentSlide].title}
                        </h2>
                        <p className="text-sm text-gray-200 text-right leading-relaxed mb-2">
                            {slides[currentSlide].description}
                        </p>

                        <div className="flex justify-end space-x-1">
                            {slides.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        index === currentSlide
                                            ? 'bg-white scale-125'
                                            : 'bg-gray-400 hover:bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
